import { LINE_ITEM_LIMITS } from '../constants/line-items';
import type {
  ExpenseLineItem,
  LineItemPick,
  LineItemsRest,
  UnpickedSplit,
} from '../types';
import { fromMinorUnits, toMinorUnits } from './amount-converter';

export interface LineItemSplitInput {
  /** Group-currency expense amount */
  amount: number;
  /** Group currency */
  currency: string;
  /** originalAmount / originalCurrency when the expense was entered in a foreign currency */
  entryAmount?: number;
  entryCurrency?: string;
  participants: readonly string[];
  /** Amounts in the entry currency */
  items: readonly Pick<
    ExpenseLineItem,
    'id' | 'amount' | 'quantity' | 'sharedBy' | 'picks'
  >[];
  rest: LineItemsRest;
  /** Weights for unpicked units and legacy rows with no sharer in the split; absent = equal */
  unpickedSplit?: UnpickedSplit;
}

export interface LineItemSplitResult {
  /** Group currency, canonical (sorted userId) order, zero shares omitted */
  splits: Array<{ userId: string; amount: number }>;
  /** Entry currency, every participant including zeros, in participants order */
  entryShares: Array<{ userId: string; amount: number }>;
  /** Entry currency, may be negative (a discount) */
  restAmount: number;
  /** Entry currency, per item id, canonical order: the sharers of a legacy row; on a picked row the pickers plus anyone with a non-zero share of the unpicked units */
  itemShares: Record<string, Array<{ userId: string; amount: number }>>;
  /** Participants whose equal share of a negative rest was clamped to 0 */
  clamped: string[];
}

// Canonical order is by userId, never display order, so the client preview and the server recompute agree on who gets each leftover unit.
const byId = (a: string, b: string) => (a < b ? -1 : a > b ? 1 : 0);

/** Largest remainder in integer units, ties to the lowest tieOrder, ranked by the exact integer remainder (BigInt above MAX_SAFE_INTEGER). Weights must be non-negative integers. Negative totals allowed; all-zero weights = equal. */
export function allocateByWeights(
  totalMinor: number,
  weights: readonly number[],
  tieOrder: readonly number[]
): number[] {
  if (weights.length === 0) return [];
  const sign = totalMinor < 0 ? -1 : 1;
  const total = Math.abs(totalMinor);
  const sum = weights.reduce((acc, w) => acc + w, 0);
  const w = sum > 0 ? weights : weights.map(() => 1);
  const wsum = sum > 0 ? sum : weights.length;
  // Exact integer arithmetic: floor = (total*w) div wsum, remainder = (total*w) mod wsum.
  // BigInt keeps it exact when total*w passes 2^53 (MAX_AMOUNT in minor units × a large weight).
  const big = total * Math.max(...w) > Number.MAX_SAFE_INTEGER;
  const floors: number[] = [];
  const rems: Array<number | bigint> = [];
  for (const x of w) {
    if (big) {
      const p = BigInt(total) * BigInt(x);
      floors.push(Number(p / BigInt(wsum)));
      rems.push(p % BigInt(wsum));
    } else {
      const p = total * x;
      floors.push(Math.floor(p / wsum));
      rems.push(p % wsum);
    }
  }
  let remainder = total - floors.reduce((acc, f) => acc + f, 0);
  const order = rems
    .map((r, i) => ({ i, r }))
    .sort((p, q) =>
      p.r === q.r ? tieOrder[p.i] - tieOrder[q.i] : p.r > q.r ? -1 : 1
    );
  for (const { i } of order) {
    if (remainder <= 0) break;
    floors[i] += 1;
    remainder -= 1;
  }
  return floors.map(f => f * sign || 0);
}

// allocateByWeights needs non-negative integers: scaling keeps fractional percentages (33.33) exact, and the cap keeps the weight sum (a plain Number) exact for hundreds of participants.
const WEIGHT_SCALE = 10_000;
const WEIGHT_MAX = 1e9;

/** One integer weight per id, renormalized over `ids` by allocateByWeights; missing, non-positive or non-finite entries are 0, and all-zero (or `equal`, or absent) is equal. */
function unpickedWeights(
  ids: readonly string[],
  split: UnpickedSplit | undefined
): number[] {
  if (!split || split.method === 'equal') return ids.map(() => 1);
  return ids.map(id => {
    const w = split.weights?.[id];
    return Number.isFinite(w) && w > 0
      ? Math.round(Math.min(w, WEIGHT_MAX) * WEIGHT_SCALE)
      : 0;
  });
}

/**
 * Splits by item, spreads the rest (proportional or equal, clamping negatives under equal), then allocates the group amount so that the splits sum to it exactly.
 * A row with `picks` prices its units by largest remainder and hands them to the pickers in userId order; unpicked units are shared by every participant by the `unpickedSplit` weights (equally without them). A row without `picks` splits equally among its sharers (everyone, by those weights, when none remain).
 * Throws RangeError unless `amount` and `entryAmount` are positive at their currency's precision, every item amount is a finite number ≥ 0, there is at least one participant, and every picked row has an integer quantity ≥ 1 with integer picks ≥ 1 summing to at most the quantity; outside those the sum invariant cannot hold.
 */
export function splitByLineItems(
  input: LineItemSplitInput
): LineItemSplitResult {
  const entryCurrency = input.entryCurrency ?? input.currency;
  const entryAmount = input.entryAmount ?? input.amount;
  const participants = [...new Set(input.participants)];
  const positive = (x: number, currency: string) =>
    Number.isFinite(x) && toMinorUnits(x, currency) > 0;
  if (!positive(input.amount, input.currency))
    throw new RangeError('splitByLineItems: amount must be positive');
  if (!positive(entryAmount, entryCurrency))
    throw new RangeError('splitByLineItems: entryAmount must be positive');
  if (participants.length === 0)
    throw new RangeError('splitByLineItems: no participants');
  if (input.items.some(i => !Number.isFinite(i.amount) || i.amount < 0))
    throw new RangeError('splitByLineItems: item amounts must be ≥ 0');
  const canonical = [...participants].sort(byId);
  const rank = new Map(canonical.map((id, i) => [id, i]));
  const tieOrder = (ids: readonly string[]) =>
    ids.map(id => rank.get(id) ?? Number.MAX_SAFE_INTEGER);
  const everyoneWeights = unpickedWeights(canonical, input.unpickedSplit);

  const entryMinor = toMinorUnits(entryAmount, entryCurrency);
  const amountMinor = toMinorUnits(input.amount, input.currency);
  const subtotal = new Map(participants.map(id => [id, 0]));
  const itemShares: LineItemSplitResult['itemShares'] = {};
  let itemsMinor = 0;
  const unitPrices = (minor: number, quantity: number) =>
    allocateByWeights(
      minor,
      Array.from({ length: quantity }, () => 1),
      Array.from({ length: quantity }, (_, i) => i)
    );

  for (const item of input.items) {
    const minor = toMinorUnits(item.amount, entryCurrency);
    itemsMinor += minor;
    const sharesMinor = new Map<string, number>();
    const listed = new Set<string>();
    if (item.picks) {
      if (!Number.isInteger(item.quantity) || item.quantity < 1)
        throw new RangeError(
          'splitByLineItems: quantity must be an integer ≥ 1'
        );
      if (item.picks.some(p => !Number.isInteger(p.units) || p.units < 1))
        throw new RangeError(
          'splitByLineItems: pick units must be integers ≥ 1'
        );
      const picks = item.picks
        .filter(p => subtotal.has(p.userId))
        .sort((a, b) => byId(a.userId, b.userId));
      const taken = picks.reduce((n, p) => n + p.units, 0);
      if (taken > item.quantity)
        throw new RangeError('splitByLineItems: picks exceed the quantity');
      const prices = unitPrices(minor, item.quantity);
      let next = 0;
      for (const pick of picks) {
        let sum = 0;
        for (let k = 0; k < pick.units; k++) sum += prices[next++];
        sharesMinor.set(pick.userId, (sharesMinor.get(pick.userId) ?? 0) + sum);
        listed.add(pick.userId);
      }
      const unpickedMinor = prices.slice(next).reduce((a, b) => a + b, 0);
      if (unpickedMinor !== 0) {
        const parts = allocateByWeights(
          unpickedMinor,
          everyoneWeights,
          tieOrder(canonical)
        );
        canonical.forEach((id, i) => {
          sharesMinor.set(id, (sharesMinor.get(id) ?? 0) + parts[i]);
          if (parts[i] !== 0) listed.add(id);
        });
      }
    } else {
      const sharerSet = new Set(item.sharedBy.filter(id => subtotal.has(id)));
      const owners = sharerSet.size
        ? canonical.filter(id => sharerSet.has(id))
        : canonical;
      const parts = allocateByWeights(
        minor,
        sharerSet.size ? owners.map(() => 1) : everyoneWeights,
        tieOrder(owners)
      );
      owners.forEach((id, i) => {
        sharesMinor.set(id, parts[i]);
        listed.add(id);
      });
    }
    itemShares[item.id] = canonical
      .filter(id => listed.has(id))
      .map(id => {
        const part = sharesMinor.get(id) ?? 0;
        subtotal.set(id, (subtotal.get(id) ?? 0) + part);
        return { userId: id, amount: fromMinorUnits(part, entryCurrency) };
      });
  }

  const restMinor = entryMinor - itemsMinor;
  const clamped: string[] = [];
  const shares = new Map(subtotal);
  if (input.rest.mode === 'proportional') {
    const parts = allocateByWeights(
      restMinor,
      participants.map(id => subtotal.get(id) ?? 0),
      tieOrder(participants)
    );
    participants.forEach((id, i) =>
      shares.set(id, (shares.get(id) ?? 0) + parts[i])
    );
  } else {
    let pool = participants;
    let toSpread = restMinor;
    for (;;) {
      const parts = allocateByWeights(
        toSpread,
        pool.map(() => 1),
        tieOrder(pool)
      );
      pool.forEach((id, i) => shares.set(id, (shares.get(id) ?? 0) + parts[i]));
      const negatives = pool.filter(id => (shares.get(id) ?? 0) < 0);
      if (negatives.length === 0 || negatives.length === pool.length) break;
      toSpread = negatives.reduce((acc, id) => acc + (shares.get(id) ?? 0), 0);
      for (const id of negatives) {
        shares.set(id, 0);
        clamped.push(id);
      }
      pool = pool.filter(id => !negatives.includes(id));
    }
  }

  const entryShares = participants.map(userId => ({
    userId,
    amount: fromMinorUnits(shares.get(userId) ?? 0, entryCurrency),
  }));
  // Same currency and same total: the entry shares are the split. Otherwise allocate the group amount by the entry shares as weights.
  const identity =
    entryCurrency === input.currency && entryMinor === amountMinor;
  const groupMinor = identity
    ? participants.map(id => shares.get(id) ?? 0)
    : allocateByWeights(
        amountMinor,
        participants.map(id => Math.max(0, shares.get(id) ?? 0)),
        tieOrder(participants)
      );
  const splits = participants
    .map((userId, i) => ({
      userId,
      amount: fromMinorUnits(groupMinor[i], input.currency),
    }))
    .filter(s => s.amount > 0)
    .sort((a, b) => byId(a.userId, b.userId));

  return {
    splits,
    entryShares,
    restAmount: fromMinorUnits(restMinor, entryCurrency),
    itemShares,
    clamped: clamped.sort(byId),
  };
}

/** `currency` is the line-item currency (originalCurrency ?? currency). Compares items (id, trimmed name, amount at precision, quantity, picks by userId, or sorted sharedBy on a legacy row), rest mode and participants; a participants change is a change (it needs itemsAllowed). */
export function lineItemsEqual(
  a: {
    items: readonly ExpenseLineItem[];
    rest: LineItemsRest;
    participants: readonly string[];
  },
  b: {
    items: readonly ExpenseLineItem[];
    rest: LineItemsRest;
    participants: readonly string[];
  },
  currency: string
): boolean {
  // JSON-encoded tuples, so free-text names cannot run into a neighbouring field or item.
  const norm = (x: typeof a) =>
    JSON.stringify([
      x.rest.mode,
      [...new Set(x.participants)].sort(byId),
      x.items
        .map(i =>
          JSON.stringify([
            i.id,
            i.name.trim(),
            toMinorUnits(i.amount, currency),
            i.quantity,
            i.picks
              ? [...i.picks]
                  .sort((p, q) => byId(p.userId, q.userId))
                  .map(p => [p.userId, p.units])
              : ['legacy', ...[...new Set(i.sharedBy)].sort(byId)],
          ])
        )
        .sort(byId),
    ]);
  return norm(a) === norm(b);
}

export const unitsOf = (
  item: Pick<ExpenseLineItem, 'picks'>,
  userId: string
): number => item.picks?.find(p => p.userId === userId)?.units ?? 0;

/** On a legacy row each sharer holds one unit, whatever the quantity. */
export const stakeOf = (
  item: Pick<ExpenseLineItem, 'sharedBy' | 'picks'>,
  userId: string
): number =>
  item.picks ? unitsOf(item, userId) : item.sharedBy.includes(userId) ? 1 : 0;

/** A legacy row (no picks) reports every unit left. */
export const unitsLeft = (
  item: Pick<ExpenseLineItem, 'quantity' | 'picks'>
): number =>
  item.picks
    ? item.quantity - item.picks.reduce((n, p) => n + p.units, 0)
    : item.quantity;

/** The most units `userId` may hold on the row; on a legacy row every other sharer keeps one. */
export const maxUnitsOf = (
  item: Pick<ExpenseLineItem, 'quantity' | 'sharedBy' | 'picks'>,
  userId: string
): number =>
  item.picks
    ? unitsOf(item, userId) + unitsLeft(item)
    : Math.max(
        0,
        item.quantity - new Set(item.sharedBy.filter(id => id !== userId)).size
      );

export const pickersOf = (
  item: Pick<ExpenseLineItem, 'sharedBy' | 'picks'>
): string[] => (item.picks ? item.picks.map(p => p.userId) : item.sharedBy);

const withPicks = (
  item: ExpenseLineItem,
  picks: LineItemPick[]
): ExpenseLineItem => ({ ...item, picks, sharedBy: picks.map(p => p.userId) });

/** Reads a legacy row as picks, one unit per sharer in `sharedBy` order while units remain, for counting only: writes go through `withUnits`, which keeps every sharer. A row that already has picks is returned as is. */
export function normalizeLegacyPicks(item: ExpenseLineItem): ExpenseLineItem {
  if (item.picks) return item;
  const picks: LineItemPick[] = [];
  for (const userId of item.sharedBy) {
    if (picks.length >= item.quantity) break;
    if (!picks.some(p => p.userId === userId)) picks.push({ userId, units: 1 });
  }
  return withPicks(item, picks);
}

/**
 * A legacy row stays legacy unless the person takes units on it. Releasing
 * edits `sharedBy` only, so the other sharers keep their equal shares. Taking
 * re-expresses the row with one unit for every other sharer, and only when
 * that fits in the quantity; otherwise someone else's share would move.
 */
function withLegacyUnits(
  item: ExpenseLineItem,
  userId: string,
  units: number
): ExpenseLineItem {
  if (units === stakeOf(item, userId)) return item;
  if (units === 0)
    return { ...item, sharedBy: item.sharedBy.filter(id => id !== userId) };
  if (units > maxUnitsOf(item, userId))
    throw new RangeError('withUnits: units out of range');
  const sharers = [...new Set(item.sharedBy)];
  const picks = sharers.map(id => ({
    userId: id,
    units: id === userId ? units : 1,
  }));
  if (!sharers.includes(userId)) picks.push({ userId, units });
  return withPicks(item, picks);
}

/** Sets `userId`'s units on the row (0 removes the pick); returns the same reference when nothing changes. Throws RangeError unless `units` is an integer between 0 and `maxUnitsOf`. */
export function withUnits(
  item: ExpenseLineItem,
  userId: string,
  units: number
): ExpenseLineItem {
  if (!Number.isInteger(units) || units < 0)
    throw new RangeError('withUnits: units out of range');
  if (!item.picks) return withLegacyUnits(item, userId, units);
  const current = unitsOf(item, userId);
  if (units > maxUnitsOf(item, userId))
    throw new RangeError('withUnits: units out of range');
  if (units === current) return item;
  const others = item.picks;
  // In place, so a stepper tap does not reorder the stored sharedBy.
  const picks =
    current > 0
      ? others.flatMap(p =>
          p.userId !== userId ? [p] : units > 0 ? [{ userId, units }] : []
        )
      : [...others, { userId, units }];
  return withPicks(item, picks);
}

/** Sets the quantity and trims picks one unit at a time from the heaviest picker (ties: lowest userId) until they fit; a legacy row only takes the quantity. Throws RangeError unless `quantity` is an integer ≥ 1. */
export function dropSurplusPicks(
  item: ExpenseLineItem,
  quantity: number
): ExpenseLineItem {
  if (!Number.isInteger(quantity) || quantity < LINE_ITEM_LIMITS.QUANTITY_MIN)
    throw new RangeError('dropSurplusPicks: quantity out of range');
  if (!item.picks)
    return quantity === item.quantity ? item : { ...item, quantity };
  const picks = item.picks.map(p => ({ ...p }));
  let surplus = picks.reduce((n, p) => n + p.units, 0) - quantity;
  if (surplus <= 0 && quantity === item.quantity) return item;
  while (surplus > 0) {
    const heaviest = picks.reduce((h, p) =>
      p.units > h.units || (p.units === h.units && p.userId < h.userId) ? p : h
    );
    heaviest.units -= 1;
    surplus -= 1;
  }
  return withPicks(
    { ...item, quantity },
    picks.filter(p => p.units > 0)
  );
}

/**
 * `wanted` is the caller's full set of units afterwards; a row left out releases their units on it. Unchanged rows keep their reference, and a legacy row follows `withUnits`: no one else's share moves.
 * Throws RangeError when a count is more than the caller may hold.
 */
export function applyLineItemPicks(
  items: readonly ExpenseLineItem[],
  userId: string,
  wanted: ReadonlyMap<string, number>
): ExpenseLineItem[] {
  return items.map(item => withUnits(item, userId, wanted.get(item.id) ?? 0));
}
